import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { AuthenticationMethodsEnum } from '../../../constants/authentication-methods.ts';
import { createParentAccountMutationRequest } from '../../../requests/create-parent-account.mutation.ts';
import { SignUpInputSchema } from '../../../types/accounts-validation.types.ts';

import { setIsRememberMe } from '../auth.slice.ts';
import { AUTH_SLICE_NAME, AuthSliceState } from '../auth.slice.types.ts';

import { postSignUpThunk } from './post-sign-up.thunk.ts';
import { signInThunk } from './sign-in.thunk.ts';

interface Props {
  email: string;
  password: string;
  preferredName: string;
}

const THUNK_NAME = `${AUTH_SLICE_NAME}/signUp`;

export const signUpThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(THUNK_NAME, async (props, thunkAPI) => {
  const { email, preferredName, password } = props;

  const input = SignUpInputSchema.safeParse({ email, preferredName, password });
  if (!input.success) {
    return;
  }

  const result = await createParentAccountMutationRequest(input.data);

  if (!result?.success) {
    const errorMessage = result.message ?? getLocaleUtil().select('auth:sign_up_failed');
    getToasterUtil().error(errorMessage);
    const error = new Error(errorMessage);
    error.name = ErrorTypeEnum.SIGN_UP_ERROR;

    getSentryUtil().trackError(
      error,
      {
        thunk: THUNK_NAME,
        errorType: ErrorTypeEnum.SIGN_UP_ERROR,
      },
      { input: { email } }
    );
    getTrackerUtil().track({
      event: TrackingEventEnum.PARENT_SIGN_UP_FAILED,
      data: {
        parentEmail: email,
        parentName: preferredName,
      },
    });
    return;
  }

  thunkAPI.dispatch(setIsRememberMe(true));
  const signUpResult = await thunkAPI.dispatch(signInThunk({ email, password, skipLoginAnalytics: true })).unwrap();
  if (signUpResult?.userId) {
    thunkAPI.dispatch(postSignUpThunk());
  }

  const currentUser = await getAwsAuthUtil().getCurrentUser();
  if (!currentUser) {
    return;
  }

  getTrackerUtil().track({
    event: TrackingEventEnum.PARENT_SIGN_UP,
    data: {
      parentEmail: email,
      parentName: preferredName,
      method: AuthenticationMethodsEnum.EMAIL,
    },
  });
});

export const signUpThunkExtraReducers = (builder: ActionReducerMapBuilder<AuthSliceState>): void => {
  builder.addCase(signUpThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(signUpThunk.fulfilled, (state) => {
    state.isLoading = false;
  });
};
